import type { Condition, ConditionGroup, SimpleCondition } from "@/types";

import type { ListFilterComparator } from "./filters";

type ListValue = SimpleCondition["value"];

/** A list row as a condition group carries it. */
export interface ListGroup {
  field: string;
  comparator: ListFilterComparator;
  /** The values in the order written, "Not set" excluded. */
  values: ListValue[];
  includeNull: boolean;
}

/** A list row about to be sent, with what its field accepts. */
export interface ListToSend extends ListGroup {
  /**
   * Whether the field takes `IS_NULL` / `IS_NOT_NULL`. A resource-id field
   * does not: the backend rejects the check, and the field is never unset.
   */
  nullChecks: boolean;
}

/** A null check, which takes no operand; the wire still requires `value`, and the backend ignores it. */
function nullCheck(
  field: string,
  comparator: "IS_NULL" | "IS_NOT_NULL",
): SimpleCondition {
  return { field, comparator, value: "" };
}

function joined(operator: "AND" | "OR", leaves: SimpleCondition[]): Condition {
  return leaves.length === 1 ? leaves[0] : { operator, conditions: leaves };
}

/**
 * The condition a list row is sent as. "Is any of" is an OR of an `EQUALS`
 * per value, with `IS_NULL` for "Not set". "Is none of" is its complement:
 * `!=` never matches a row whose field is unset, so without "Not set" the
 * unset rows are added back, `OR(AND(!=…), IS_NULL)`, and with it they are
 * left out explicitly, `AND(!=…, IS_NOT_NULL)`. A one-member list is that
 * leaf alone.
 *
 * On a field that takes no null checks, "Not set" beside a value changes
 * nothing (the field is never unset) and is left out, as is the `IS_NULL`
 * branch. "Not set" alone is still sent as its check, since nothing else says
 * it; the picker never offers it there.
 */
export function listToCondition({
  field,
  comparator,
  values,
  includeNull,
  nullChecks,
}: ListToSend): Condition {
  const notSet = includeNull && (nullChecks || values.length === 0);
  if (comparator === "ANY_OF") {
    return joined("OR", [
      ...values.map(
        (value): SimpleCondition => ({
          field,
          comparator: "EQUALS",
          value,
        }),
      ),
      ...(notSet ? [nullCheck(field, "IS_NULL")] : []),
    ]);
  }
  const excluded = values.map(
    (value): SimpleCondition => ({ field, comparator: "NOT_EQUALS", value }),
  );
  if (notSet) {
    return joined("AND", [...excluded, nullCheck(field, "IS_NOT_NULL")]);
  }
  const differs = joined("AND", excluded);
  return nullChecks
    ? { operator: "OR", conditions: [differs, nullCheck(field, "IS_NULL")] }
    : differs;
}

function isGroup(condition: Condition): condition is ConditionGroup {
  return "operator" in condition;
}

/**
 * The leaves of `conditions` when every one is a leaf on one field, the
 * field's comparators limited to `member` and at most one `notSet` check, with
 * at least one `member`. Null otherwise.
 */
function sameFieldLeaves(
  conditions: readonly Condition[],
  member: "EQUALS" | "NOT_EQUALS",
  notSet: "IS_NULL" | "IS_NOT_NULL",
): { field: string; values: ListValue[]; hasNotSet: boolean } | null {
  if (conditions.some(isGroup)) {
    return null;
  }
  const leaves = conditions as SimpleCondition[];
  const field = leaves[0]?.field;
  const members = leaves.filter((leaf) => leaf.comparator === member);
  const notSets = leaves.filter((leaf) => leaf.comparator === notSet);
  if (
    field === undefined ||
    leaves.some((leaf) => leaf.field !== field) ||
    members.length === 0 ||
    notSets.length > 1 ||
    members.length + notSets.length !== leaves.length
  ) {
    return null;
  }
  return {
    field,
    values: members.map((leaf) => leaf.value),
    hasNotSet: notSets.length === 1,
  };
}

/**
 * "Is none of" without "Not set", as {@link listToCondition} sends it: an OR
 * of the `!=` leaf (or an AND of `!=` leaves) and an `IS_NULL` on the same
 * field, in either order.
 */
function noneOfKeepingUnset(group: ConditionGroup): ListGroup | null {
  if (group.operator !== "OR" || group.conditions.length !== 2) {
    return null;
  }
  const nullIndex = group.conditions.findIndex(
    (child) => !isGroup(child) && child.comparator === "IS_NULL",
  );
  if (nullIndex === -1) {
    return null;
  }
  const isNull = group.conditions[nullIndex] as SimpleCondition;
  const differs = group.conditions[1 - nullIndex];
  if (
    isGroup(differs) &&
    (differs.operator !== "AND" || differs.conditions.length < 2)
  ) {
    return null;
  }
  const excluded = sameFieldLeaves(
    isGroup(differs) ? differs.conditions : [differs],
    "NOT_EQUALS",
    "IS_NOT_NULL",
  );
  if (
    excluded === null ||
    excluded.hasNotSet ||
    excluded.field !== isNull.field
  ) {
    return null;
  }
  return {
    field: excluded.field,
    comparator: "NONE_OF",
    values: excluded.values,
    includeNull: false,
  };
}

/**
 * The list row a group spells, or null when it spells none. Reads every
 * group {@link listToCondition} sends, members in the order written:
 *
 * - `OR(EQUALS…, ≤1 IS_NULL)` on one field is "is any of", with "Not set"
 *   when `IS_NULL` is there.
 * - `OR(AND(!=…), IS_NULL)` and `OR(!=, IS_NULL)` are "is none of" without
 *   "Not set".
 * - `AND(!=…, ≤1 IS_NOT_NULL)` is "is none of" with "Not set", whether or not
 *   `IS_NOT_NULL` is there: `!=` already leaves the unset rows out.
 *
 * Never reads a bare leaf: a one-member list is indistinguishable from the
 * single comparison it is sent as.
 */
export function conditionToList(group: ConditionGroup): ListGroup | null {
  if (group.conditions.length < 2) {
    return null;
  }
  if (group.operator === "OR") {
    const anyOf = sameFieldLeaves(group.conditions, "EQUALS", "IS_NULL");
    return anyOf === null
      ? noneOfKeepingUnset(group)
      : {
          field: anyOf.field,
          comparator: "ANY_OF",
          values: anyOf.values,
          includeNull: anyOf.hasNotSet,
        };
  }
  if (group.operator === "AND") {
    const noneOf = sameFieldLeaves(
      group.conditions,
      "NOT_EQUALS",
      "IS_NOT_NULL",
    );
    return noneOf === null
      ? null
      : {
          field: noneOf.field,
          comparator: "NONE_OF",
          values: noneOf.values,
          includeNull: true,
        };
  }
  return null;
}
